/**
 * ErrorFallback - Componente de fallback para erros não tratados
 * 
 * RELEASE: 2026-01-27
 * VERSION: 1.0.0
 * 
 * Exibido quando um erro não tratado ocorre na aplicação.
 * Integrado com Sentry ErrorBoundary para captura automática.
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorFallbackProps {
  error?: Error | unknown;
  resetError?: () => void;
}

/**
 * Componente de fallback para erros críticos
 * 
 * @param error - Erro que causou o fallback (opcional)
 * @param resetError - Função para tentar recuperar do erro
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  // Handler para recarregar a página
  const handleReload = () => {
    window.location.reload();
  };
  
  // Handler para voltar ao início
  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Ops! Algo deu errado</CardTitle>
          <CardDescription>
            Ocorreu um erro inesperado. Nossa equipe já foi notificada.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Mostra detalhes do erro em desenvolvimento */}
          {import.meta.env.DEV && error && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {error instanceof Error ? error.message : String(error)}
              </p>
            </div>
          )}
          
          <div className="flex flex-col gap-2">
            {/* Botão para tentar novamente */}
            {resetError ? (
              <Button onClick={resetError} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Tentar novamente
              </Button>
            ) : (
              <Button onClick={handleReload} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Recarregar página
              </Button>
            )}
            
            {/* Botão para voltar ao início */}
            <Button variant="outline" onClick={handleGoHome} className="w-full">
              <Home className="mr-2 h-4 w-4" />
              Voltar ao início
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground">
            Se o problema persistir, entre em contato com o suporte.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
